
	import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
	import Modal from '@/components/Modal.vue';
	import SearchSVG from '@/components/SearchSVG.vue';
	import { Observer } from 'mobx-vue';
	import PatientRow from '@/components/patient-list/PatientRow.vue';
	import Loading from '@/components/Loading.vue';
	import { ProgressInfo } from '@/components/patient-list/PatientTable.vue';
	import { Patient } from '@/store/engine/openapi/generated';
	import UtilsStore from '@/store/utilsStore';

	function stringLiterals<T extends string>(...args: T[]): T[] {
		return args;
	}

	type ElementType<T extends ReadonlyArray<unknown>> = T extends ReadonlyArray<infer ElementType> ? ElementType : never;

	const types = stringLiterals('by name', 'by patient Id', 'by order#');

	@Observer
	@Component({
		components: { PatientRow, Modal, SearchSVG, Loading }
	})
	export default class SearchModal extends Vue {
		@Prop({ default: {} }) patients: { [key: string]: Patient } | undefined;
		@Prop({ default: false }) loading;
		@Prop({ required: true }) progressInfo: ProgressInfo | undefined;
		private searchTypes = types;
		private searchInput: string = '';
		private searchType: ElementType<typeof types> = types[0];
		private lastSearch: { input: string; searchType: ElementType<typeof types> } | undefined;
		private res: any[] = [];
		private criteriaChanged = false;

		@Watch('searchInput')
		@Watch('searchType')
		private onCriteriaChanged() {
			this.criteriaChanged = true;
		}
		// eslint-disable-next-line no-useless-escape
		private regex: RegExp = /\<|\>|\:|\“|\"|\/|\\|\||\?|\*/;

		private patientClick(patient) {
			this.$emit('patientClick', patient);
		}

		@Watch('progressInfo.updateNeeded', { deep: true })
		handleUpdate() {
			if (this.progressInfo?.updateNeeded && this.patients && !this.criteriaChanged) {
				if (this.lastSearch?.input && this.lastSearch?.searchType !== undefined) {
					this.search(false, this.lastSearch.input, this.lastSearch?.searchType);
				}
			}
		}

		private get showLoading() {
			if (this.loading) {
				return true;
			}
			return !!(
				this.searchType === 'by order#' &&
				!this.criteriaChanged &&
				this.progressInfo &&
				this.progressInfo.percentDone !== '100' &&
				!Object.keys(this.res).length
			);
		}

		private get noResults() {
			if (!Object.keys(this.res).length && this.lastSearch && !this.criteriaChanged) {
				if (this.searchType === 'by order#') {
					return this.progressInfo && this.progressInfo.percentDone === '100';
				} else {
					return true;
				}
			}
			return false;
		}

		private async search(fixScroll: boolean = true, input: string = this.searchInput, searchType: ElementType<typeof types> = this.searchType) {
			this.$emit('updated');
			this.criteriaChanged = false;
			if (this.patients && input.length >= 2 && !this.loading) {
				const el = this.$refs.table;
				if (el instanceof Element && fixScroll) {
					el.scrollTop = 0;
				}
				this.res = [];

				this.lastSearch = {
					input,
					searchType
				};

				switch (this.searchTypes.indexOf(searchType)) {
					case 0: {
						// By name
						if (this.regex.test(input)) return;
						let toFind = input.split(/[, ]+/g).map((r) => r.toLowerCase()); // Find all string to find
						toFind = [...new Set(toFind)]; // Remove duplications
						if (this.patients) {
							this.res = Object.values(this.patients).filter((p: Patient) => {
								if (UtilsStore.getHighestPriorityStatus(p)?.includes('ORDER')) {
									p['silhouette'] = true;
								}
								return toFind.every((r) =>
									(
										(p &&
											p.user &&
											p.user.firstName &&
											p.user.lastName &&
											p.user.firstName.toLowerCase() + ' ' + p.user.lastName.toLowerCase()) || ['']
									).includes(r)
								);
							});
						}
						break;
					}
					case 1: {
						// By User ID
						let toFind = new Array<string>();
						toFind.push(input.toLowerCase(), input.replaceAll(/[, -]+/g, '').toLowerCase()); // Add whole string and string with deleted splitters
						toFind = [...new Set(toFind)]; // Remove duplications

						if (this.patients) {
							this.res = Object.values(this.patients).filter((p: Patient) => {
								if (UtilsStore.getHighestPriorityStatus(p)?.includes('ORDER')) {
									p['silhouette'] = true;
								}
								return toFind.some(
									(r) =>
										p.medicalSolutionData &&
										p.medicalSolutionData.patientID &&
										p.medicalSolutionData.patientID
											.replaceAll(/[-]+/g, '')
											.toLowerCase()
											.includes(r)
								); // Check if at least one string exists
							});
						}
						break;
					}
					case 2: {
						// By Order ID
						let reg = new Array<string>();
						reg.push(input.toLowerCase(), input.replaceAll(/[, -]+/g, '').toLowerCase()); // Add whole string and string with deleted splitters
						reg = [...new Set(reg)]; // Remove duplications

						if (this.patients) {
							this.res = Object.values(this.patients).filter((patient: Patient) => {
								if (patient.hasOwnProperty('orders')) {
									for (const order of patient['orders']) {
										if (patient['orders'].length && order) {
											const isOk = reg.some((r) =>
												order['id']
													.replaceAll(/[-]+/g, '')
													.toLowerCase()
													.includes(r)
											);
											if (isOk) {
												delete patient['silhouette'];
												patient['order_id'] = order['id'] || undefined; // Assign needed fields
												patient['updateTime'] = order['time'] || undefined; // Assign needed fields
												return true;
											} // Check if matches with search
										}
									}
									return false;
								}
							});
						}
						break;
					}
					default:
						return;
				}

				if (this.searchTypes.indexOf(searchType) === 2) return; // No need for duplicates with same patient

				let sortNeeded = false;

				for (const p of Object.values(this.res)) {
					if (p.hasOwnProperty('orders')) {
						if (p['orders'].length) {
							for (const o of p['orders']) {
								const patientCopy = JSON.parse(JSON.stringify(p));
								patientCopy['orderFirstName'] = o['firstName'] || patientCopy.user.firstName; // Assign needed fields
								patientCopy['orderLastName'] = o['lastName'] || patientCopy.user.lastName; // Assign needed fields
								patientCopy['order_id'] = o.id || undefined; // Assign needed fields
								patientCopy['updateTime'] = o.time || undefined; // Assign needed fields

								delete patientCopy.silhouette;

								delete patientCopy.orders;

								sortNeeded = true;

								this.res.push(patientCopy);
							}
						}
					}
				}

				if (sortNeeded) {
					const p = (t) => Date.parse(t);
					this.res.sort((a, b) => {
						let timeA = p(a.updateTime) || p(a.sessionsStatuses[0].updateTime);
						let timeB = p(b.updateTime) || p(b.sessionsStatuses[0].updateTime);
						return timeB - timeA;
					});
				}
			}
		}
	}
